const signupPanel = (function () {
	const isValidEmail = emailAddress => {
		if (!emailAddress) {
			return false;
		}

		const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.([^<>()[\]\\.,;:\s@"]+))*)|(".+"))@((\[[0-9]{1,3}(\.[0-9]{1,3}){3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
		return emailRegex.test(emailAddress);
	};

	const setSignupEmailError = (errorElement, message) => {
		if (!errorElement) {
			return;
		}

		errorElement.textContent = message;
		errorElement.classList.remove('display-none');
	};

	const setLoadingState = (button, isLoading) => {
		if (!button) {
			return;
		}

		button.disabled = isLoading;
		button.classList.toggle('btn--loading', isLoading);
		button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
	};

	const ensureAntiForgeryToken = form => {
		if (!form) {
			return Promise.resolve(false);
		}

		const antiForgeryUrl = form.getAttribute('data-anti-forgery-url');
		const antiForgeryTokenField = form.querySelector('input[name="__RequestVerificationToken"]');

		if (!antiForgeryUrl || !antiForgeryTokenField) {
			return Promise.resolve(false);
		}

		if (form.getAttribute('data-anti-forgery-token-loaded') === 'true') {
			return Promise.resolve(true);
		}

		if (form.__antiForgeryBootstrapPromise) {
			return form.__antiForgeryBootstrapPromise;
		}

		form.__antiForgeryBootstrapPromise = fetch(antiForgeryUrl, {
			method: 'GET',
			credentials: 'same-origin',
			headers: {
				'Accept': 'application/json',
			},
		})
			.then(async response => {
				if (!response.ok) {
					return false;
				}

				const responseJson = await response.json();
				if (!responseJson || !responseJson.formToken) {
					return false;
				}

				antiForgeryTokenField.value = responseJson.formToken;
				form.setAttribute('data-anti-forgery-token-loaded', 'true');
				return true;
			})
			.catch(() => false)
			.finally(() => {
				form.__antiForgeryBootstrapPromise = null;
			});

		return form.__antiForgeryBootstrapPromise;
	};

	const showSignupState = (form, stateName, hidePreamble = false) => {
		const initialState = form.querySelector('[data-signup-state="initial"]');
		const accountState = form.querySelector('[data-signup-state="account"]');
		const noAccountState = form.querySelector('[data-signup-state="no-account"]');
		const preambleElements = form.querySelectorAll('[data-signup-preamble]');

		if (initialState) {
			initialState.classList.toggle('display-none', stateName !== 'initial');
		}

		if (accountState) {
			accountState.classList.toggle('display-none', stateName !== 'account');
		}

		if (noAccountState) {
			noAccountState.classList.toggle('display-none', stateName !== 'no-account');
		}

		preambleElements.forEach(el => {
			el.classList.toggle('display-none', hidePreamble);
		});
	};

	const setSignupConsentError = (form, show) => {
		const consentError = form.querySelector('[data-signup-consent-error]');
		const consentFieldGroup = form.querySelector('[data-signup-consent-group]');

		if (!consentError || !consentFieldGroup) {
			return;
		}

		if (show) {
			consentError.classList.remove('display-none');
			consentFieldGroup.classList.add('field-group--error');
			return;
		}

		consentError.classList.add('display-none');
		consentFieldGroup.classList.remove('field-group--error');
	};

	const validateSignupEmailInput = field => {
		if (!field) {
			return false;
		}

		const emailValue = field.value.trim();
		const isValid = emailValue !== '' && isValidEmail(emailValue);
		const fieldGroup = field.closest('.field-group');
		const errorMessage = fieldGroup ? fieldGroup.querySelector('#signupEmail__error') : null;

		if (errorMessage && errorMessage.dataset.defaultMessage) {
			errorMessage.textContent = errorMessage.dataset.defaultMessage;
		}

		field.value = emailValue;

		if (!isValid) {
			if (fieldGroup) {
				fieldGroup.classList.add('field-group--error');
			}
			field.setAttribute('aria-invalid', 'true');
			if (errorMessage) {
				errorMessage.classList.remove('display-none');
			}
			return false;
		}

		if (fieldGroup) {
			fieldGroup.classList.remove('field-group--error');
		}
		field.setAttribute('aria-invalid', 'false');
		if (errorMessage) {
			errorMessage.classList.add('display-none');
		}
		return true;
	};

	const setupSignupEmailValidation = form => {
		if (!form) {
			return;
		}

		const signupEmailField = form.querySelector('#signupEmail');
		if (!signupEmailField) {
			return;
		}

		const errorMessage = form.querySelector('#signupEmail__error');
		if (errorMessage && !errorMessage.dataset.defaultMessage) {
			errorMessage.dataset.defaultMessage = errorMessage.textContent.trim();
		}

		signupEmailField.addEventListener('focusout', () => {
			validateSignupEmailInput(signupEmailField);
		});
	};

	const checkSignupAccountExists = async (form, emailAddress) => {
		const accountCheckUrl = form.getAttribute('data-account-check-url');
		const antiForgeryTokenField = form.querySelector('input[name="__RequestVerificationToken"]');

		if (!accountCheckUrl || !antiForgeryTokenField) {
			return { success: false, accountExists: false };
		}

		if (!await ensureAntiForgeryToken(form)) {
			return { success: false, accountExists: false };
		}

		const payload = new URLSearchParams();
		payload.append('__RequestVerificationToken', antiForgeryTokenField.value);
		payload.append('emailAddress', emailAddress);

		try {
			const response = await fetch(accountCheckUrl, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
				},
				body: payload.toString(),
				credentials: 'same-origin',
			});

			if (!response.ok) {
				return { success: false, accountExists: false, identityGuid: '' };
			}

			const responseJson = await response.json();
			const accountExists = responseJson && responseJson.accountExists === true;

			return {
				success: responseJson && responseJson.success === true,
				accountExists: accountExists,
				identityGuid: responseJson ? responseJson.identity_guid : '',
			};
		} catch (error) {
			return { success: false, accountExists: false, identityGuid: '' };
		}
	};

	const submitSignupCapture = async (form, emailAddress, consentedToEmail) => {
		const signupCaptureUrl = form.getAttribute('data-signup-capture-url');
		const antiForgeryTokenField = form.querySelector('input[name="__RequestVerificationToken"]');

		if (!signupCaptureUrl || !antiForgeryTokenField) {
			return { success: false };
		}

		if (!await ensureAntiForgeryToken(form)) {
			return { success: false };
		}

		const payload = new URLSearchParams();
		payload.append('__RequestVerificationToken', antiForgeryTokenField.value);
		payload.append('emailAddress', emailAddress);
		payload.append('consentedToEmail', String(consentedToEmail));

		try {
			const response = await fetch(signupCaptureUrl, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
				},
				body: payload.toString(),
				credentials: 'same-origin',
			});

			if (!response.ok) {
				return { success: false };
			}

			const responseJson = await response.json();
			return {
				success: responseJson && responseJson.success === true,
				identityGuid: responseJson ? responseJson.identity_guid : '',
			};
		} catch (error) {
			return { success: false };
		}
	};

	const getSignupAuthUrl = form => {
		if (!form) {
			return '';
		}

		const signInButton = form.querySelector('#btn__checkValidity--signinCreate');
		if (!signInButton) {
			return '';
		}

		return signInButton.getAttribute('data-signin-url') || '';
	};

	const handleSignupEmail = async (form, button) => {
		const signupEmailField = form.querySelector('#signupEmail');
		const signupEmailError = form.querySelector('#signupEmail__error');

		if (!validateSignupEmailInput(signupEmailField)) {
			return;
		}

		setLoadingState(button, true);

		const accountCheckResult = await checkSignupAccountExists(form, signupEmailField.value);
		if (!accountCheckResult.success) {
			setLoadingState(button, false);
			setSignupEmailError(signupEmailError, 'There was a problem checking your account. Please try again.');
			return;
		}

		setLoadingState(button, false);

		if (accountCheckResult.accountExists) {
			showSignupState(form, 'account');
			if (accountCheckResult.identityGuid) {
				form.dispatchEvent(new CustomEvent('signup-panel:capture-success', {
					detail: {
						identityGuid: accountCheckResult.identityGuid,
					},
				}));
			}
			return;
		}

		showSignupState(form, 'no-account');
	};

	const handleSignupCaptureSubmit = async (form, button) => {
		const signupEmailField = form.querySelector('#signupEmail');
		const signupEmailError = form.querySelector('#signupEmail__error');
		const signupConsentCheckbox = form.querySelector('#signupConsentCheckbox');

		if (!validateSignupEmailInput(signupEmailField)) {
			return;
		}

		if (!signupConsentCheckbox || signupConsentCheckbox.checked !== true) {
			setSignupConsentError(form, true);
			return;
		}

		setSignupConsentError(form, false);

		setLoadingState(button, true);
		const signupResult = await submitSignupCapture(form, signupEmailField.value, signupConsentCheckbox.checked);
		setLoadingState(button, false);

		if (!signupResult.success) {
			setSignupEmailError(signupEmailError, 'There was a problem submitting your email. Please try again.');
			showSignupState(form, 'initial');
			return;
		}

		const accountPanel = form.querySelector('[data-signup-state="account"]');
		if (accountPanel) {
			accountPanel.classList.add('mod-signup-panel__state-panel--no-border');
		}
		showSignupState(form, 'account', true);
		form.dispatchEvent(new CustomEvent('signup-panel:capture-success', {
			detail: {
				identityGuid: signupResult.identityGuid,
			},
		}));
	};

	const resetSignupEmailInput = form => {
		const signupEmail = form.querySelector('#signupEmail');
		const signupEmailError = form.querySelector('#signupEmail__error');
		const signupConsentCheckbox = form.querySelector('#signupConsentCheckbox');

		if (!signupEmail) {
			return;
		}

		signupEmail.value = '';
		const fieldGroup = signupEmail.closest('.field-group');
		if (fieldGroup) {
			fieldGroup.classList.remove('field-group--error');
		}
		signupEmail.setAttribute('aria-invalid', 'false');
		if (signupEmailError) {
			signupEmailError.classList.add('display-none');
		}

		if (signupConsentCheckbox) {
			signupConsentCheckbox.checked = false;
		}

		setSignupConsentError(form, false);
		showSignupState(form, 'initial');
	};

	const setupSignupPanelEvents = form => {
		if (!form || form.getAttribute('data-signup-panel-events-bound') === 'true') {
			return;
		}

		form.setAttribute('data-signup-panel-events-bound', 'true');
		ensureAntiForgeryToken(form);
		setupSignupEmailValidation(form);

		const signupContinueButton = form.querySelector('#btn__checkValidity--signup');
		if (signupContinueButton) {
			signupContinueButton.addEventListener('click', async event => {
				event.preventDefault();
				await handleSignupEmail(form, signupContinueButton);
			});
		}

		const signupSubmitButton = form.querySelector('#btn__checkValidity--signupSubmit');
		if (signupSubmitButton) {
			signupSubmitButton.addEventListener('click', async event => {
				event.preventDefault();
				await handleSignupCaptureSubmit(form, signupSubmitButton);
			});
		}

		const signInButton = form.querySelector('#btn__checkValidity--signinCreate');
		if (signInButton) {
			signInButton.addEventListener('click', event => {
				event.preventDefault();
				const signInUrl = getSignupAuthUrl(form);
				if (signInUrl) {
					window.location.href = signInUrl;
				}
			});
		}

		form.addEventListener('signup-panel:reset', () => {
			resetSignupEmailInput(form);
		});
	};

	const init = () => {
		const panels = document.querySelectorAll('.mod-signup-panel');
		panels.forEach(panel => {
			const form = panel.closest('form');
			if (!form) {
				return;
			}

			setupSignupPanelEvents(form);
		});
	};

	return {
		init,
	};

})();

document.addEventListener('DOMContentLoaded', () => {
	signupPanel.init();
});
